import React, { useEffect, useState } from "react";
import { Box, Button, Typography, Dialog, DialogContent } from "@mui/material";
import DeleteIcon from "../assets/images/Delete.svg";
import { baseFetch } from "../helpers/apiClient";
import moment from "moment";
import Loader from "./Loader";

export default function CancelSubscription({
  open,
  handleClose,
  handleConfirm,
  loading,
  userType, // "individual" or "corporate"
}) {
  const [loader, setLoader] = useState(false);
  const [cancelInfo, setCancelInfo] = useState({ endDate: "" });
  const {
    endDate,
    isProrated,
    refundAmount,
    endRetentionDate,
    subscriptionInterval,
  } = cancelInfo;
  // Generate dynamic message based on props
  const getMessage = () => {
    if (userType === "individual" && isProrated) {
      return (
        <>
          <Typography color="#2A4876" fontWeight="bold" mb={1}>
            Sad to see you go!
          </Typography>
          <Typography color="#2A4876">
            Your plan remains active until <b>{endDate}</b>, and you'll still
            have full access until then. You will also receive a prorated refund
            of <b>${refundAmount}</b>.
          </Typography>
          <Typography color="#2A4876" mt={2}>
            <b>Resubscribe anytime</b> to regain access to Echo's premium
            professional development tools and features! We hope you enjoy
            everything the Free Plan has to offer.
          </Typography>
        </>
      );
    }

    if (userType === "individual" && !isProrated) {
      return (
        <>
          <Typography color="#2A4876" fontWeight="bold" mb={1}>
            Sad to see you go!
          </Typography>
          <Typography color="#2A4876">
            Your plan remains active until <b>{endDate}</b>, the end of
            {subscriptionInterval === "month"
              ? " the current billing cycle on your monthly plan. No refund applies. "
              : " your yearly subscription. Since it’s been over 3 months, no refund applies."}
          </Typography>
          <Typography color="#2A4876" mt={2}>
            <b>Resubscribe anytime</b> to regain access to Echo's premium
            professional development tools and features! We hope you enjoy
            everything the Free Plan has to offer.
          </Typography>
        </>
      );
    }

    if (userType === "corporate" && isProrated) {
      return (
        <>
          <Typography color="#2A4876" fontWeight="bold" mb={1}>
            Sad to see you go!
          </Typography>
          <Typography color="#2A4876">
            Your plan remains active until <b>{endDate}</b>, and you'll still
            have full access until then. You will also receive a prorated refund
            of <b>${refundAmount}</b>.
          </Typography>
          <Typography color="#2A4876" mt={2}>
            <b>Heads up:</b> Content retention fees start after your plan ends
            based on your library size. To avoid fees, renew your plan or clear
            your content before{" "}
            <b>{moment(endRetentionDate || endDate).format("DD MMM YYYY")}</b>.
          </Typography>
        </>
      );
    }

    if (userType === "corporate" && !isProrated) {
      return (
        <>
          <Typography color="#2A4876" fontWeight="bold" mb={1}>
            Sad to see you go!
          </Typography>
          <Typography color="#2A4876">
            Your plan remains active until <b>{endDate}</b>, the end of your
            {subscriptionInterval === "month"
              ? " monthly subscription. Since it’s monthly plan, no refund applies."
              : " yearly subscription. Since it’s been over 3 months, no refund applies."}
          </Typography>
          <Typography color="#2A4876" mt={2}>
            <b>Heads up:</b> Content retention fees start after your plan ends
            based on your library size. To avoid fees, renew your plan or clear
            your content before{" "}
            <b>{moment(endRetentionDate || endDate)?.format("DD MMM YYYY")}</b>.
          </Typography>
        </>
      );
    }
  };

  useEffect(() => {
    const getCancelPlanInfo = () => {
      setLoader(true);
      baseFetch({
        url: "/v1/web/subscription/cancelInfo",
        method: "GET",
      })
        .then((response) => {
          if (response.status) {
            const obj = {
              endDate: moment(response?.data?.cancelAt).format("DD MMM YYYY"),
              isProrated: response?.data?.refundAmount > 0 ? true : false,
              refundAmount: response?.data?.refundAmount / 100,
              endRetentionDate: response?.data?.endRetentionDate
                ? moment(response?.data?.endRetentionDate).format("DD MMM YYYY")
                : "",
              subscriptionInterval: response?.data?.subscriptionInterval,
            };
            setCancelInfo(obj);
          }
        })
        .catch((err) => console.error(err))
        .finally(() => {
          setLoader(false);
        });
    };
    getCancelPlanInfo();
  }, []);

  return (
    <Box>
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: "34px",
            overflow: "hidden",
          },
        }}
      >
        {loader ? (
          <Loader />
        ) : (
          <DialogContent
            sx={{
              textAlign: "center",
              padding: "21px 35px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* Icon */}
            <Box
              sx={{
                width: "70px",
                height: "70px",
                borderRadius: "50%",
                backgroundColor: "#F1F7FA",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <img src={DeleteIcon} alt="delete-icon" />
            </Box>

            {/* Dynamic Message */}
            {getMessage()}

            {/* Buttons */}
            <Box mt={3} display="flex" justifyContent="center" gap={2}>
              <Button
                onClick={handleClose}
                sx={{
                  textTransform: "none",
                  borderRadius: "34px",
                  backgroundColor: "#AAB6C8",
                  color: "#2A4876",
                  fontWeight: "bold",
                  padding: "12px 44px",
                  "&:hover": { backgroundColor: "#C4CED8" },
                }}
              >
                Back
              </Button>
              <Button
                onClick={handleConfirm}
                sx={{
                  textTransform: "none",
                  borderRadius: "34px",
                  backgroundColor: "#2A4876",
                  color: "#FFFFFF",
                  fontWeight: "bold",
                  padding: "12px 44px",
                  "&:hover": { backgroundColor: "#002A5C" },
                }}
                loading={loading}
              >
                Continue
              </Button>
            </Box>
          </DialogContent>
        )}
      </Dialog>
    </Box>
  );
}
